import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMAGES, imgUrl } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];
const HEADLINE_LINES = ["Africa and the Caribbean", "Have More Than History", "in Common."];

const RouteMotif = () => (
  <svg
    className="pointer-events-none absolute -right-24 bottom-10 hidden h-72 w-72 opacity-25 lg:block"
    viewBox="0 0 300 300"
    fill="none"
    aria-hidden="true"
  >
    <path d="M10 260 C 90 180, 150 220, 290 40" stroke="#C5A059" strokeWidth="1.5" strokeDasharray="1 8" strokeLinecap="round" />
    <path d="M10 230 C 110 150, 180 190, 290 10" stroke="#C5A059" strokeWidth="1" strokeDasharray="1 8" strokeLinecap="round" opacity="0.6" />
    <circle cx="10" cy="260" r="4" fill="#C5A059" />
    <circle cx="290" cy="40" r="4" fill="#C5A059" />
  </svg>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const imageY = useTransform(scrollY, [0, 900], [0, 180]);

  return (
    <section id="home" data-testid="hero-section" className="grain relative overflow-hidden bg-atlantic">
      <motion.div style={{ y: imageY }} className="absolute inset-0">
        <img
          src={imgUrl(IMAGES.hero, 2000)}
          alt="Aerial view of a working Atlantic container port"
          className="h-[115%] w-full object-cover"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 bg-atlantic/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-atlantic via-atlantic/45 to-atlantic/40" />
      <RouteMotif />
      <div className="relative mx-auto flex min-h-[86vh] max-w-7xl flex-col justify-end px-5 pb-20 pt-44 lg:px-8">
        <motion.p
          data-testid="hero-kicker"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
          className="text-[11px] font-semibold uppercase tracking-[0.32em] text-gold sm:text-xs"
        >
          Independent Journalism · Research · Public Policy
        </motion.p>
        <h1
          data-testid="hero-headline"
          className="mt-6 max-w-4xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl"
        >
          {HEADLINE_LINES.map((line, i) => (
            <span key={line} className="-mb-1 block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, ease: EASE, delay: 0.3 + i * 0.14 }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          data-testid="hero-subheading"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
          className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg"
        >
          Atlantic Bridge Institute produces independent journalism, research and public-interest projects that connect
          African and Caribbean societies, institutions and people.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1 }}
          className="mt-10 flex flex-wrap items-stretch gap-4"
        >
          <Link
            data-testid="hero-explore-button"
            to="/#news"
            className="bg-gold px-7 py-3.5 text-sm font-semibold tracking-wide text-atlantic transition-colors hover:bg-gold-light"
          >
            Explore Our Work
          </Link>
          <Link
            data-testid="hero-dossier-button"
            to="/countries/jamaica"
            className="border border-white/50 px-7 py-3.5 text-sm font-semibold tracking-wide text-white transition-colors hover:border-white hover:bg-white/10"
          >
            Read the Jamaica Dossier
          </Link>
          <Link
            data-testid="hero-subscribe-link"
            to="/#newsletter"
            className="group flex items-center gap-2 border border-gold/70 px-7 py-3.5 text-sm font-semibold tracking-wide text-gold transition-colors hover:bg-gold hover:text-atlantic"
          >
            Subscribe to Atlantic Bridge Briefing
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
